import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Edit, Save, X, Award, Flame, Zap, ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trashProfile, trashUpdateProfile, trashSubscribe, humanErr } from "@/lib/trashApi";

export default function TrashProfile() {
  const { user, logout } = useAuth();
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ bio: "", banner_url: "", city: "", country: "",
                                       socials: { twitter: "", instagram: "", tiktok: "", youtube: "" } });
  const [busy, setBusy] = useState(false);

  const load = () => user && trashProfile(user.id).then((d) => {
    setP(d);
    setForm({
      bio: d.bio || "", banner_url: d.banner_url || "",
      city: d.city || "", country: d.country || "",
      socials: { twitter: "", instagram: "", tiktok: "", youtube: "", ...(d.socials || {}) },
    });
  }).catch(() => {});
  useEffect(() => { load(); }, [user]);

  if (!user) return (
    <div className="py-16 text-center">
      <div className="font-black text-2xl mb-2">Sign in to see your profile</div>
      <Link to="/login?next=/trashhunter/me" className="inline-flex items-center gap-1 bg-[#39FF14] text-black font-black text-xs uppercase tracking-widest px-5 py-2.5 rounded-full mt-3">Sign in</Link>
    </div>
  );
  if (!p) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      await trashUpdateProfile(form);
      toast.success("Profile updated");
      setEditing(false); load();
    } catch (e) { toast.error(humanErr(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const toggleElite = async () => {
    setBusy(true);
    try {
      const r = await trashSubscribe(p.is_elite ? "cancel" : "subscribe");
      toast.success(r.is_elite ? "Elite subscription active" : "Subscription cancelled");
      load();
    } catch (e) { toast.error(humanErr(e, "Subscribe failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="trash-profile">
      <section className="rounded-3xl overflow-hidden border-2 border-white/10 relative">
        <div className="h-32 relative"
             style={{
               background: p.banner_url
                 ? `url(${p.banner_url}) center/cover`
                 : "linear-gradient(135deg, #063d1d, #001509, #0F0326)",
             }} />
        <div className="p-5 -mt-10 relative">
          <div className="w-20 h-20 rounded-2xl border-4 border-black shadow-lg flex items-center justify-center font-black text-3xl"
               style={{ background: p.user.avatar_color }}>{p.user.name?.[0]?.toUpperCase() || "?"}</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <div className="font-black text-2xl">{p.user.name}</div>
            {p.is_verified_member && <ShieldCheck className="w-5 h-5 text-[#39FF14]" title="Verified" />}
            {p.is_elite && <span className="text-[9px] mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFD700] text-black font-black">Elite</span>}
          </div>
          <div className="text-xs mono uppercase tracking-widest text-white/50">Level {p.level} · {p.level_title}</div>
          <div className="mt-1.5 h-1.5 bg-white/10 rounded-full overflow-hidden max-w-xs">
            <div className="h-full bg-[#39FF14]" style={{ width: `${Math.min(100, (p.xp_into_level / p.xp_needed_next) * 100)}%` }} />
          </div>
          {p.bio && <div className="text-sm text-white/80 mt-3 whitespace-pre-wrap">{p.bio}</div>}
          {(p.city || p.country) && <div className="text-xs text-white/50 mt-1">{[p.city, p.country].filter(Boolean).join(" · ")}</div>}
          <div className="flex gap-2 mt-4">
            <button data-testid="profile-edit-btn" onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-[10px] uppercase tracking-widest px-4 py-2 rounded-full">
              <Edit className="w-3 h-3" /> Edit profile
            </button>
            <button data-testid="profile-logout" onClick={logout}
              className="inline-flex items-center gap-1 border border-white/10 bg-white/5 text-white/60 font-black text-[10px] uppercase tracking-widest px-4 py-2 rounded-full">
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-2">
        <Stat label="Cleanups" value={p.cleanups_total} accent="#39FF14" />
        <Stat label="Verified" value={p.verified_count} accent="#00F0FF" />
        <Stat label="Streak" value={<span className="inline-flex items-center gap-1"><Flame className="w-4 h-4" />{p.streak_day}d</span>} accent="#FFD700" />
        <Stat label="Best streak" value={`${p.longest_streak}d`} accent="#FF00E5" />
      </div>

      <section className="rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-4" data-testid="profile-elite-card">
        <div className="flex items-start gap-3">
          <Zap className="w-6 h-6 text-[#FFD700] mt-1" />
          <div className="flex-1">
            <div className="font-black text-lg">Elite membership</div>
            <div className="text-xs text-white/70 mt-1">$10.99/mo · 2× XP, 1.25× coin bonuses, exclusive badges + themes, early feature access. SIMULATED payment in preview.</div>
            <button data-testid="profile-elite-toggle" onClick={toggleElite} disabled={busy}
              className={"mt-3 inline-flex items-center gap-1 font-black text-xs uppercase tracking-widest px-5 py-2 rounded-full disabled:opacity-50 " +
                (p.is_elite ? "border border-white/20 bg-white/5" : "bg-[#FFD700] text-black")}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              {p.is_elite ? "Cancel Elite" : "Subscribe to Elite"}
            </button>
          </div>
        </div>
      </section>

      <section>
        <div className="font-black text-sm uppercase tracking-widest text-white/80 mb-2 flex items-center gap-1">
          <Award className="w-4 h-4" /> Badges
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="profile-badges">
          {p.badges?.map((b) => (
            <div key={b.key} data-testid={`badge-${b.key}`}
              className={"rounded-2xl border p-3 text-center " + (b.unlocked ? "border-[#39FF14]/40 bg-[#39FF14]/10" : "border-white/10 bg-black/40 opacity-40")}>
              <Award className={"w-6 h-6 mx-auto mb-1 " + (b.unlocked ? "text-[#39FF14]" : "text-white/40")} />
              <div className="font-black text-[11px] leading-tight">{b.title}</div>
              <div className="text-[9px] text-white/50 mt-1">{b.description}</div>
            </div>
          ))}
        </div>
      </section>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={() => setEditing(false)} data-testid="profile-edit-modal">
          <div className="w-full max-w-md rounded-3xl border-2 border-[#39FF14]/40 bg-[#001509] p-5 space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <div className="font-black text-lg flex-1">Edit profile</div>
              <button onClick={() => setEditing(false)} className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <Field label="Bio">
              <textarea data-testid="edit-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm" maxLength={400} />
            </Field>
            <Field label="Banner image URL"><input data-testid="edit-banner" value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm" /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="City"><input data-testid="edit-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm" /></Field>
              <Field label="Country"><input data-testid="edit-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm" /></Field>
            </div>
            <div className="text-[10px] mono uppercase tracking-widest text-white/50">Socials (optional)</div>
            {["twitter","instagram","tiktok","youtube"].map((k) => (
              <input key={k} placeholder={`${k} handle or URL`} value={form.socials?.[k] || ""}
                onChange={(e) => setForm({ ...form, socials: { ...form.socials, [k]: e.target.value } })}
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-xs" />
            ))}
            <button data-testid="edit-save" onClick={save} disabled={busy}
              className="w-full bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <><Save className="w-4 h-4 inline mr-1" /> Save</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="text-[10px] mono uppercase tracking-widest text-white/50 mb-1">{label}</div>{children}</label>;
}
function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/40 p-3 text-center">
      <div className="text-[9px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-black text-xl mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}
